"use client";

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Drawer } from "vaul";
import { cn } from "@/lib/utils";
import { useReturnFocus } from "@/components/ui/dialog";
import { shouldIgnoreShortcutTarget } from "@/components/shell/should-ignore-shortcut-target";
import { sportGroups, type LeagueTab, type PickerCategory, type PickerItem, type PickerPeriod } from "./view-model";

export function ignoreBoardKey(target: EventTarget | null): boolean {
  return shouldIgnoreShortcutTarget(target) || (target instanceof Element && target.closest('[role="dialog"], [role="menu"], [role="listbox"]') !== null);
}

const JAKARTA = "'Plus Jakarta Sans', Inter, system-ui, sans-serif";

function Chevron({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
  );
}

function ChevronRight({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
  );
}

function SearchIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
  );
}

export function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => { if (ref.current && !ref.current.contains(event.target as Node)) close(); };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, close]);
  return ref;
}

export function ScrollFade({ className, children }: { className?: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [fade, setFade] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => { el.removeEventListener("scroll", update); observer.disconnect(); };
  }, [children]);
  return (
    <div ref={ref} className={cn(className, fade && "[mask-image:linear-gradient(to_right,#000_calc(100%-28px),transparent)]")}>
      {children}
    </div>
  );
}

const FILL_STYLE = {
  top: "calc(var(--visible-top, 0px) + env(safe-area-inset-top) + 0.5rem)",
  height: "calc(var(--visible-height, 100dvh) - env(safe-area-inset-top) - 0.5rem)",
};

function fitVisibleViewport(node: HTMLDivElement) {
  const viewport = window.visualViewport;
  if (!viewport) return;
  const fit = () => {
    node.style.setProperty("--visible-top", `${viewport.offsetTop}px`);
    node.style.setProperty("--visible-height", `${viewport.height}px`);
  };
  fit();
  viewport.addEventListener("resize", fit);
  viewport.addEventListener("scroll", fit);
  return () => {
    viewport.removeEventListener("resize", fit);
    viewport.removeEventListener("scroll", fit);
  };
}

export function SheetShell({
  open, title, onClose, children, header, maxHeight = "85svh", fill = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  header?: React.ReactNode;
  maxHeight?: string;
  fill?: boolean;
}) {
  const returnFocus = useReturnFocus({});
  const stopFit = useRef<(() => void) | undefined>(undefined);
  const fitRef = useCallback((node: HTMLDivElement | null) => {
    stopFit.current?.();
    stopFit.current = node ? fitVisibleViewport(node) : undefined;
  }, []);
  return (
    <Drawer.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }} repositionInputs={!fill}>
      <Drawer.Portal>
        <Drawer.Overlay className="odds-black fixed inset-0 z-50 bg-[rgba(8,9,10,0.72)]" />
        <Drawer.Content
          {...returnFocus}
          ref={fill ? fitRef : undefined}
          aria-describedby={undefined}
          onPointerDownOutside={(event) => { if (event.target instanceof Element && event.target.closest("[data-toast-viewport]")) event.preventDefault(); }}
          style={fill ? FILL_STYLE : { maxHeight }}
          className="odds-black fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-surface-raised shadow-[0_-1px_0_rgba(255,255,255,0.08),0_-24px_48px_rgba(0,0,0,0.5)] outline-none"
        >
          <div className="flex h-6 flex-shrink-0 items-center justify-center pt-1"><span className="h-1 w-9 rounded-full bg-white/20" /></div>
          {header === undefined ? (
            <Drawer.Title className="flex h-11 flex-shrink-0 items-center px-4 text-[15px] font-semibold text-foreground">{title}</Drawer.Title>
          ) : (
            <>
              <Drawer.Title className="sr-only">{title}</Drawer.Title>
              {header}
            </>
          )}
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function rowClass(isMobile: boolean) {
  return cn("flex w-full items-center justify-between gap-2 rounded-md px-2.5 text-left text-[13px] font-medium", isMobile ? "min-h-11 py-1.5" : "min-h-8 py-1");
}

function categoryRowClass(isMobile: boolean, shown: boolean, active: boolean) {
  return cn(rowClass(isMobile), shown ? "bg-white/[0.07] text-foreground" : active ? "text-primary hover:bg-white/[0.04]" : "text-zinc-300 hover:bg-white/[0.04] hover:text-foreground");
}

function itemRowClass(isMobile: boolean, active: boolean, highlighted: boolean) {
  return cn(rowClass(isMobile), active ? "bg-primary/10 text-primary" : highlighted ? "bg-white/[0.06] text-foreground" : "text-zinc-300 hover:bg-white/[0.04] hover:text-foreground");
}

function listKeys<T>(list: readonly T[], active: number, setActive: React.Dispatch<React.SetStateAction<number>>, choose: (item: T) => void) {
  return (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") { event.preventDefault(); setActive((i) => Math.min(i + 1, list.length - 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (event.key === "Enter") { event.preventDefault(); const chosen = list[active]; if (chosen !== undefined) choose(chosen); }
  };
}

function PickerShell({
  title, open, isMobile, animate = true, trigger, query, onQuery, onSearchKey, onClose, left, right, footer,
}: {
  title: string;
  open: boolean;
  isMobile: boolean;
  animate?: boolean;
  trigger: React.ReactNode;
  query: string;
  onQuery: (query: string) => void;
  onSearchKey: (event: React.KeyboardEvent) => void;
  onClose: () => void;
  left: React.ReactNode;
  right: readonly React.ReactNode[];
  footer?: React.ReactNode;
}) {
  const ref = useDismiss(open, onClose);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (open && !isMobile) inputRef.current?.focus(); }, [open, isMobile]);

  const noun = title.toLowerCase();
  const searching = query.trim() !== "";
  const rows = searching && right.length === 0 ? <div className="px-3 py-8 text-center text-[12px] text-zinc-500">{`No ${noun} match`}</div> : right;

  const searchBar = (
    <div className={cn("flex flex-shrink-0 items-center gap-2 border-surface-line", isMobile ? "mx-4 mb-2 h-11 rounded-lg border bg-surface-base px-3" : "h-11 border-b px-3")}>
      <span className="flex text-zinc-500"><SearchIcon size={14} /></span>
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        onKeyDown={onSearchKey}
        placeholder={`Search ${noun}`}
        aria-label={`Search ${noun}`}
        className={cn("min-w-0 flex-grow bg-transparent text-foreground outline-none placeholder:text-zinc-500 [&::-webkit-search-cancel-button]:hidden", isMobile ? "text-[16px]" : "text-[13px]")}
      />
    </div>
  );

  const frame = isMobile ? cn("min-h-0 flex-grow", footer ? "pb-2" : "pb-[max(1rem,env(safe-area-inset-bottom))]") : "max-h-[min(460px,calc(100vh-132px))]";
  const body = searching ? (
    <div className={cn(frame, "overflow-y-auto p-1.5")}>{rows}</div>
  ) : (
    <div className={cn(frame, "grid grid-rows-[minmax(0,1fr)]", isMobile ? "grid-cols-[132px_minmax(0,1fr)]" : "grid-cols-[200px_minmax(0,1fr)]")}>
      <div className="overflow-y-auto border-r border-white/[0.06] p-1.5">{left}</div>
      <div className="overflow-y-auto p-1.5">{rows}</div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <SheetShell open={open} title={title} onClose={onClose}>
          {searchBar}
          {body}
          {footer}
        </SheetShell>
      </>
    );
  }

  return (
    <div ref={ref} className="relative flex-shrink-0">
      {trigger}
      {open && (
        <div role="menu" className={cn("absolute left-0 top-[calc(100%+6px)] z-30 w-[560px] max-w-[calc(100vw-32px)] origin-top-left rounded-[10px] border border-surface-line bg-surface-overlay shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)]", animate && "animate-in fade-in-0 zoom-in-95 duration-150")}>
          {searchBar}
          {body}
          {footer}
        </div>
      )}
    </div>
  );
}

export function LeagueMenu({
  leagues, activeLabel, isMobile, onNavigate,
}: {
  leagues: readonly LeagueTab[];
  activeLabel: string;
  isMobile: boolean;
  onNavigate: (id: string) => void;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hover, setHover] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const close = useCallback(() => { setOpen(false); setQuery(""); setHover(null); }, []);

  const q = query.trim().toLowerCase();
  const groups = sportGroups(leagues);
  const branches = groups.filter((group) => group.leagues.length > 1);
  const current = branches.find((group) => group.key === hover) ?? branches.find((group) => group.active) ?? branches[0];
  const hits = groups.flatMap((group) => group.leagues.filter((league) => [league.label, league.id, group.label].some((text) => text.toLowerCase().includes(q))));
  const list = q === "" ? current?.leagues ?? [] : hits;

  useEffect(() => { setActive(0); }, [query, hover]);

  const leagueLink = (league: LeagueTab, label: string, className: string) => (
    <Link
      key={league.id}
      href={`/odds-screen/${league.id}`}
      aria-current={league.active ? "page" : undefined}
      onClick={() => { onNavigate(league.id); close(); }}
      className={className}
    >
      <span className="line-clamp-2 leading-snug">{label}</span>
      <span className="data-num text-[11px] text-zinc-500">{league.events}</span>
    </Link>
  );

  const trigger = isMobile ? (
    <button type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)} className="flex h-11 min-w-0 items-center gap-1 rounded-lg pr-1 text-left" style={{ fontFamily: JAKARTA, fontWeight: 700 }}>
      <span className="truncate text-[17px] text-foreground">{activeLabel}</span>
      <span className="flex text-zinc-500"><Chevron size={16} /></span>
    </button>
  ) : (
    <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="tap flex h-8 items-center gap-1 rounded-md px-1.5 text-[13px] font-semibold text-foreground hover:bg-white/[0.04]">
      <span>{activeLabel}</span>
      <span className="flex text-zinc-500"><Chevron size={14} /></span>
    </button>
  );

  const left = (
    <div className="flex flex-col gap-0.5">
      {groups.map((group) => (group.leagues.length === 1 ? leagueLink(group.leagues[0], group.leagues[0].label, itemRowClass(isMobile, group.active, false)) : (
        <button
          key={group.key}
          type="button"
          aria-current={group === current ? "true" : undefined}
          onMouseEnter={() => setHover(group.key)}
          onFocus={() => setHover(group.key)}
          onClick={() => setHover(group.key)}
          className={categoryRowClass(isMobile, group === current, group.active)}
        >
          <span className="line-clamp-2 leading-snug">{group.label}</span>
          <span className="flex flex-shrink-0 text-zinc-600"><ChevronRight /></span>
        </button>
      )))}
    </div>
  );

  return (
    <PickerShell
      title="Leagues"
      open={open}
      isMobile={isMobile}
      trigger={trigger}
      query={query}
      onQuery={setQuery}
      onSearchKey={listKeys(list, active, setActive, (league) => { onNavigate(league.id); close(); router.push(`/odds-screen/${league.id}`); })}
      onClose={close}
      left={left}
      right={list.map((league, index) => leagueLink(league, league.label, itemRowClass(isMobile, league.active, !isMobile && index === active)))}
    />
  );
}

const SECTIONS = ["Game", "Player props"] as const;

export function MarketMenu({
  label, categories, skipped, isMobile, onPick,
}: {
  label: string;
  categories: readonly PickerCategory[];
  skipped: number;
  isMobile: boolean;
  onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [animate, setAnimate] = useState(true);
  const [hover, setHover] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const close = useCallback(() => { setOpen(false); setQuery(""); setHover(null); }, []);

  const q = query.trim().toLowerCase();
  const directId = (category: PickerCategory) => (isMobile && (category.items[0]?.periods.length ?? 0) > 1 ? null : category.opens);
  const branches = categories.filter((category) => directId(category) === null);
  const current = branches.find((category) => category.key === hover) ?? branches.find((category) => category.active) ?? branches[0];
  const hits = useMemo<readonly PickerItem[]>(
    () => {
      if (q === "") return [];
      const found = categories.flatMap((category) => category.items.flatMap((item): { item: PickerItem; category: string }[] => {
        if (item.label.toLowerCase().includes(q) || category.label.toLowerCase().includes(q)) return [{ item, category: category.label }];
        return item.periods
          .filter((period) => period.label.toLowerCase().includes(q))
          .map((period) => ({ item: { id: period.id, label: `${item.label} · ${period.label}`, active: period.active, periods: [] }, category: category.label }));
      }));
      const seen = new Map<string, number>();
      const key = (hit: { item: PickerItem }) => hit.item.label.toLowerCase();
      for (const hit of found) seen.set(key(hit), (seen.get(key(hit)) ?? 0) + 1);
      return found.map((hit) => ((seen.get(key(hit)) ?? 0) > 1 ? { ...hit.item, label: `${hit.item.label} · ${hit.category}` } : hit.item));
    },
    [categories, q],
  );
  const list: readonly PickerItem[] = q === "" ? current?.items ?? [] : hits;

  useEffect(() => { setActive(0); }, [query, hover]);

  useEffect(() => {
    if (open || isMobile) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "m" && event.key !== "M") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (ignoreBoardKey(event.target)) return;
      event.preventDefault();
      setAnimate(false);
      setOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, isMobile]);

  const pick = (id: string) => { onPick(id); close(); };
  const openByClick = () => { setAnimate(true); setOpen((v) => !v); };

  const trigger = isMobile ? (
    <button type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)} className="flex h-11 min-w-0 items-center gap-1 rounded-lg pr-1 text-left" style={{ fontFamily: JAKARTA, fontWeight: 700 }}>
      <span className="truncate text-[17px] text-foreground">{label}</span>
      <span className="flex flex-shrink-0 text-zinc-500"><Chevron size={16} /></span>
    </button>
  ) : (
    <button type="button" aria-haspopup="menu" aria-expanded={open} aria-keyshortcuts="M" title="Markets · M" onClick={openByClick} className={cn("tap flex h-8 items-center gap-1 rounded-md px-1.5 text-[13px] font-semibold text-foreground hover:bg-white/[0.04]", open && "bg-white/[0.06]")}>
      <span>{label}</span>
      <span className="flex text-zinc-500"><Chevron size={14} /></span>
    </button>
  );

  const leftColumn = (
    <div className="flex flex-col gap-0.5">
      {SECTIONS.map((section) => {
        const members = categories.filter((category) => category.section === section);
        if (members.length === 0) return null;
        return (
          <Fragment key={section}>
            <span className="px-2.5 pb-1 pt-2 text-[10px] font-medium uppercase tracking-[0.08em] text-zinc-500">{section}</span>
            {members.map((category) => (
              <button
                key={category.key}
                type="button"
                aria-current={category === current ? "true" : undefined}
                onMouseEnter={() => { if (directId(category) === null) setHover(category.key); }}
                onFocus={() => { if (directId(category) === null) setHover(category.key); }}
                onClick={() => { const direct = directId(category); if (direct === null) setHover(category.key); else pick(direct); }}
                className={categoryRowClass(isMobile, category === current, category.active)}
              >
                <span className="line-clamp-2 leading-snug">{category.label}</span>
                {directId(category) === null && <span className="flex flex-shrink-0 text-zinc-600"><ChevronRight /></span>}
              </button>
            ))}
          </Fragment>
        );
      })}
    </div>
  );

  const itemButton = (item: PickerItem, highlighted: boolean) => (
    <button
      key={item.id}
      type="button"
      role="menuitem"
      onClick={() => pick(item.id)}
      className={itemRowClass(isMobile, item.active, highlighted)}
    >
      <span className="line-clamp-2 leading-snug">{item.label}</span>
    </button>
  );

  const periodRow = (period: PickerPeriod, indent: boolean) => (
    <button
      key={period.id}
      type="button"
      role="menuitem"
      onClick={() => pick(period.id)}
      className={cn(
        "flex min-h-11 w-full items-center gap-2 rounded-md py-1.5 pr-2.5 text-left text-[13px] font-medium",
        indent ? "pl-5" : "pl-2.5",
        period.active ? "bg-primary/10 text-primary" : "text-zinc-300 hover:bg-white/[0.04] hover:text-foreground",
      )}
    >
      <span className="line-clamp-2 leading-snug">{period.label}</span>
    </button>
  );

  const marketBlock = (item: PickerItem, heading: boolean) => (item.periods.length < 2 ? itemButton(item, false) : (
    <div key={item.id} role="group" aria-label={item.label}>
      {heading && <div className="px-2.5 pb-0.5 pt-3 text-[12px] font-semibold text-foreground">{item.label}</div>}
      {item.periods.map((period) => periodRow(period, heading))}
    </div>
  ));

  const rightColumn = list.map((item, index) => (isMobile ? marketBlock(item, q !== "" || current?.opens === null) : itemButton(item, index === active)));

  const footer = skipped > 0 && (
    <div className={cn("flex-shrink-0 border-t border-surface-line px-3 py-2 text-[11px] font-medium text-zinc-500", isMobile && "pb-[max(0.75rem,env(safe-area-inset-bottom))]")}>
      {skipped === 1 ? "1 market needs an app update" : `${skipped} markets need an app update`}
    </div>
  );

  return (
    <PickerShell
      title="Markets"
      open={open}
      isMobile={isMobile}
      animate={animate}
      trigger={trigger}
      query={query}
      onQuery={setQuery}
      onSearchKey={listKeys(list, active, setActive, (item) => pick(item.id))}
      onClose={close}
      left={leftColumn}
      right={rightColumn}
      footer={footer}
    />
  );
}
